import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { adminAPI as sharedAdminAPI } from '../services/api';
import { useOperationsAPI } from '../context/useOperationsAPI';
import LoadingSpinner from './LoadingSpinner';
import EntityDrawer from './ui/EntityDrawer';

type VehicleType =
  | 'sedan'
  | 'suv'
  | 'van'
  | 'sprinter'
  | 'minibus'
  | 'motorcoach'
  | 'truck';

type VehicleStatus = 'available' | 'maintenance' | 'rented';

type VehicleFormData = {
  make: string;
  model: string;
  year: number;
  fleetNumber: string;
  licensePlate: string;
  vin: string;
  hourlyRate: string;
  currentMileage: number;
  vehicleType: VehicleType;
  color: string;
  capacity: number;
  status: VehicleStatus;
};

type VehicleDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

type VehicleEditDrawerProps = VehicleDrawerProps & {
  vehicle: any;
};

const VEHICLE_TYPES: VehicleType[] = [
  'sedan',
  'suv',
  'van',
  'sprinter',
  'minibus',
  'motorcoach',
  'truck',
];

const VEHICLE_STATUSES: VehicleStatus[] = [
  'available',
  'rented',
  'maintenance',
];

function emptyVehicleForm(): VehicleFormData {
  return {
    make: '',
    model: '',
    year: new Date().getFullYear(),
    fleetNumber: '',
    licensePlate: '',
    vin: '',
    hourlyRate: '',
    currentMileage: 0,
    vehicleType: 'van',
    color: '',
    capacity: 15,
    status: 'available',
  };
}

function labelize(value?: string | null) {
  return (value || 'N/A')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeVehicleForm(vehicle: any): VehicleFormData {
  return {
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    year: vehicle?.year || new Date().getFullYear(),
    fleetNumber: vehicle?.fleetNumber || vehicle?.fleet_number || '',
    licensePlate: vehicle?.licensePlate || vehicle?.license_plate || '',
    vin: vehicle?.vin || '',
    hourlyRate: String(vehicle?.hourlyRate || vehicle?.hourly_rate || ''),
    currentMileage: Number(
      vehicle?.currentMileage ?? vehicle?.current_mileage ?? 0
    ),
    vehicleType: vehicle?.vehicleType || vehicle?.vehicle_type || 'van',
    color: vehicle?.color || '',
    capacity: Number(vehicle?.capacity ?? vehicle?.passenger_capacity ?? 15),
    status: vehicle?.status || 'available',
  };
}

function statusClass(status: VehicleStatus) {
  switch (status) {
    case 'available':
      return 'bg-success-container text-success-on-container';
    case 'rented':
      return 'bg-info-container text-info-on-container';
    case 'maintenance':
      return 'bg-warning-container text-warning-on-container';
    default:
      return 'bg-surface-container-low text-on-surface-variant';
  }
}

function statusIcon(status: VehicleStatus) {
  switch (status) {
    case 'available':
      return <CheckCircleIcon className="h-4 w-4" />;
    case 'maintenance':
      return <WrenchScrewdriverIcon className="h-4 w-4" />;
    default:
      return <TruckIcon className="h-4 w-4" />;
  }
}

function buildCreatePayload(data: VehicleFormData) {
  return {
    make: data.make,
    model: data.model,
    year: data.year,
    fleetNumber: data.fleetNumber,
    licensePlate: data.licensePlate,
    vin: data.vin,
    location: '',
    hourlyRate: data.hourlyRate,
    dailyRate: '',
    features: [],
    type: 'gas' as const,
    vehicleType: data.vehicleType,
    color: data.color,
    capacity: data.capacity,
  };
}

function VehicleFields({
  value,
  onChange,
  includeStatus = false,
}: {
  value: VehicleFormData;
  onChange: (next: VehicleFormData) => void;
  includeStatus?: boolean;
}) {
  const setField = <K extends keyof VehicleFormData>(
    key: K,
    nextValue: VehicleFormData[K]
  ) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <div className="space-y-5">
      <section className="admin-subpanel rounded-card p-4">
        <h3 className="text-sm font-semibold text-on-surface">
          Vehicle Identity
        </h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Fleet #
            </span>
            <input
              className="input-field mt-1"
              value={value.fleetNumber}
              onChange={(event) =>
                setField('fleetNumber', event.target.value.toUpperCase())
              }
              placeholder="245"
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Vehicle Type
            </span>
            <select
              className="input-field mt-1"
              value={value.vehicleType}
              onChange={(event) =>
                setField('vehicleType', event.target.value as VehicleType)
              }
            >
              {VEHICLE_TYPES.map((vehicleType) => (
                <option key={vehicleType} value={vehicleType}>
                  {labelize(vehicleType)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Make *
            </span>
            <input
              required
              className="input-field mt-1"
              value={value.make}
              onChange={(event) => setField('make', event.target.value)}
              placeholder="Mercedes-Benz"
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Model *
            </span>
            <input
              required
              className="input-field mt-1"
              value={value.model}
              onChange={(event) => setField('model', event.target.value)}
              placeholder="S-Class"
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Year *
            </span>
            <input
              required
              type="number"
              min="1900"
              max={new Date().getFullYear() + 2}
              className="input-field mt-1"
              value={value.year}
              onChange={(event) => setField('year', Number(event.target.value))}
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Color
            </span>
            <input
              className="input-field mt-1"
              value={value.color}
              onChange={(event) => setField('color', event.target.value)}
              placeholder="Black"
            />
          </label>
        </div>
      </section>

      <section className="admin-subpanel rounded-card p-4">
        <h3 className="text-sm font-semibold text-on-surface">Operations</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              License Plate *
            </span>
            <input
              required
              className="input-field mt-1"
              value={value.licensePlate}
              onChange={(event) =>
                setField('licensePlate', event.target.value.toUpperCase())
              }
              placeholder="ABC1234"
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              VIN
            </span>
            <input
              maxLength={17}
              className="input-field mt-1 font-mono"
              value={value.vin}
              onChange={(event) =>
                setField('vin', event.target.value.toUpperCase())
              }
              placeholder="17-character VIN"
            />
            <span className="mt-1 block text-xs text-on-surface-variant">
              Optional until the exact vehicle record is complete.
            </span>
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Passenger Capacity *
            </span>
            <input
              required
              type="number"
              min="1"
              max="80"
              className="input-field mt-1"
              value={value.capacity}
              onChange={(event) =>
                setField('capacity', Number(event.target.value))
              }
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Current Mileage
            </span>
            <input
              type="number"
              min="0"
              className="input-field mt-1"
              value={value.currentMileage}
              onChange={(event) =>
                setField('currentMileage', Number(event.target.value))
              }
            />
          </label>
          <label>
            <span className="text-xs font-medium text-on-surface-variant">
              Hourly Rate *
            </span>
            <input
              required
              type="number"
              min="0"
              step="0.01"
              className="input-field mt-1"
              value={value.hourlyRate}
              onChange={(event) => setField('hourlyRate', event.target.value)}
              placeholder="95.00"
            />
          </label>
          {includeStatus ? (
            <label>
              <span className="text-xs font-medium text-on-surface-variant">
                Status
              </span>
              <select
                className="input-field mt-1"
                value={value.status}
                onChange={(event) =>
                  setField('status', event.target.value as VehicleStatus)
                }
              >
                {VEHICLE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {labelize(status)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function VehicleCreateDrawer({ isOpen, onClose }: VehicleDrawerProps) {
  const adminAPI = useOperationsAPI(sharedAdminAPI);
  const [vehicleData, setVehicleData] = useState<VehicleFormData>(
    emptyVehicleForm()
  );
  const queryClient = useQueryClient();

  const createVehicleMutation = useMutation(
    (data: VehicleFormData) =>
      adminAPI.createVehicle?.(buildCreatePayload(data)) ||
      Promise.resolve({ data: { success: true } }),
    {
      onSuccess: () => {
        toast.success('Vehicle created');
        queryClient.invalidateQueries('admin-vehicles');
        setVehicleData(emptyVehicleForm());
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Failed to create vehicle'
        );
      },
    }
  );

  const subtitle = useMemo(() => {
    return `${labelize(vehicleData.vehicleType)} · ${
      vehicleData.capacity || 0
    } seats`;
  }, [vehicleData.capacity, vehicleData.vehicleType]);

  return (
    <EntityDrawer
      open={isOpen}
      onClose={onClose}
      title="Add Vehicle"
      subtitle={subtitle}
      widthClassName="max-w-3xl"
      headerActions={
        <span
          className={`inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-semibold ${statusClass(
            vehicleData.status
          )}`}
        >
          {statusIcon(vehicleData.status)}
          {labelize(vehicleData.status)}
        </span>
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="vehicle-create-form"
            disabled={createVehicleMutation.isLoading}
            className="btn-primary inline-flex items-center gap-2"
          >
            {createVehicleMutation.isLoading ? (
              <LoadingSpinner size="sm" />
            ) : null}
            Create Vehicle
          </button>
        </>
      }
    >
      <form
        id="vehicle-create-form"
        onSubmit={(event) => {
          event.preventDefault();
          createVehicleMutation.mutate(vehicleData);
        }}
      >
        <VehicleFields value={vehicleData} onChange={setVehicleData} />
      </form>
    </EntityDrawer>
  );
}

export function VehicleEditDrawer({
  isOpen,
  onClose,
  vehicle,
}: VehicleEditDrawerProps) {
  const adminAPI = useOperationsAPI(sharedAdminAPI);
  const [vehicleData, setVehicleData] = useState<VehicleFormData>(
    emptyVehicleForm()
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (vehicle) {
      setVehicleData(normalizeVehicleForm(vehicle));
    }
  }, [vehicle]);

  const updateVehicleMutation = useMutation(
    (data: VehicleFormData) =>
      adminAPI.updateVehicle?.(vehicle?.id, data) ||
      Promise.resolve({ data: { success: true } }),
    {
      onSuccess: () => {
        toast.success('Vehicle updated');
        queryClient.invalidateQueries('admin-vehicles');
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Failed to update vehicle'
        );
      },
    }
  );

  if (!vehicle) return null;

  const vehicleName =
    [vehicleData.make, vehicleData.model].filter(Boolean).join(' ') ||
    'Vehicle';

  return (
    <EntityDrawer
      open={isOpen}
      onClose={onClose}
      title={vehicleName}
      subtitle={`${
        vehicleData.fleetNumber ? `#${vehicleData.fleetNumber} · ` : ''
      }${labelize(vehicleData.vehicleType)} · ${
        vehicleData.capacity || 0
      } seats`}
      widthClassName="max-w-3xl"
      headerActions={
        <span
          className={`inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-semibold ${statusClass(
            vehicleData.status
          )}`}
        >
          {statusIcon(vehicleData.status)}
          {labelize(vehicleData.status)}
        </span>
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="submit"
            form="vehicle-edit-form"
            disabled={updateVehicleMutation.isLoading}
            className="btn-primary inline-flex items-center gap-2"
          >
            {updateVehicleMutation.isLoading ? (
              <LoadingSpinner size="sm" />
            ) : null}
            Save Vehicle
          </button>
        </>
      }
    >
      <form
        id="vehicle-edit-form"
        onSubmit={(event) => {
          event.preventDefault();
          updateVehicleMutation.mutate(vehicleData);
        }}
      >
        <VehicleFields
          value={vehicleData}
          onChange={setVehicleData}
          includeStatus
        />
      </form>
    </EntityDrawer>
  );
}
